import React, { useEffect, useState } from 'react'
import { Button } from 'react-bootstrap'
import { FaClock, FaRegSquarePlus } from "react-icons/fa6";
import toast from 'react-hot-toast'
import CustomisedModal from '../../components/Modal/CustomisedModal';
import { Box, Paper, Tooltip } from "@mui/material";
import DataTable from "react-data-table-component";
import { FiClock, FiCheckCircle, FiXCircle, FiTrash2, FiNavigation, FiCalendar, FiList, FiFile, FiFilePlus, FiBellOff, FiBell, FiAlertTriangle, FiSend, FiArrowRight } from 'react-icons/fi';
import { getAllNotification, scheduleAutoNotification, toggleAutoNotificationStatus } from '../../api/context/autoNotifications';
import Loader from '../../components/loader/loader';
import BulkNotificationModal from './AutoNotificationModal';
import CreateUpdateAutoNotificationModal from './CreateUpdateAutoNotificationModal';
import { Switch } from '@mui/material';

const queueDatatableCustomStyles = {
    headRow: {
        style: {
            backgroundColor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            minHeight: '46px',
        },
    },
    headCells: {
        style: {
            display: 'flex',
            justifyContent: 'center',
            fontWeight: 600,
            fontSize: '10px',
            padding: '10px 8px',
            color: '#475569',
            textTransform: 'uppercase',
            letterSpacing: '0.02em',
            backgroundColor: '#f8fafc',
        },
    },
    cells: {
        style: {
            display: 'flex',
            fontSize: '11px',
            fontWeight: 500,
            color: '#0f172a',
            padding: '10px 8px',
        },
    },
    rows: {
        style: {
            minHeight: '48px',
            borderBottom: '1px solid #eef2f7',
            transition: 'background 0.15s ease',
            '&:not(:last-of-type)': { borderBottom: '1px solid #eef2f7' },
            '&:hover': { backgroundColor: '#f8fafc !important', cursor: 'pointer' },
        },
    },
    pagination: {
        style: {
            borderTop: '1px solid #eef2f7',
            minHeight: '48px',
            fontSize: '12px',
            color: '#475569',
        },
    },
};

const AutoNotification = () => {
    const [data, setData] = useState([]);
    const [update, setUpdate] = useState(false)
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [limit, setLimit] = useState(10);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [initial, setInitial] = useState(false)
    const [deleteModal, setDeleteModal] = useState(false);
    const [deletingId, setDeletingId] = useState("");
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortDirection, setSortDirection] = useState('DESC');
    const [activeTab, setActiveTab] = useState('upcoming'); // Default to upcoming
    const [isInitialRender, setIsInitialRender] = useState(true);
    const [showBulkModal, setShowBulkModal] = useState(false);

    // Unified modal state
    const [notificationModal, setNotificationModal] = useState(false);
    const [modalMode, setModalMode] = useState('view'); // 'view', 'edit', 'create'
    const [saving, setSaving] = useState(false);

    const toggleDeleteModal = () => {
        setDeleteModal(!deleteModal);
    };

    const statusActive = (id) => {
        // Store current state for potential rollback
        const previousNotifications = [...data];

        // Optimistically update UI immediately
        setData(prevNotifications =>
            prevNotifications.map(item =>
                item.id === id
                    ? { ...item, isActive: !item.isActive }
                    : item
            )
        );

        // Then make API call
        toggleAutoNotificationStatus(id)((response) => {
            if (response?.data?.statusCode !== 200 && response?.status !== 200) {
                setData(previousNotifications);
                toast.error("Failed to update notification status");
            } else {
                toast.success("Notification status updated successfully");
            }
        });
    }

    // Handle row click - open in view mode
    const handleRowClick = (row) => {
        setSelectedNotification(row);
        setNotificationModal(true);
    };

    // Handle edit button click
    const handleEditClick = () => {
        setModalMode('edit');
    };

    const canUpdateNotification = (notification) => {
        if (!notification) return false;

        const now = new Date();
        const scheduledTime = new Date(notification?.scheduledDateTime);
        const status = getNotificationStatus(notification);

        // Allow updates only for upcoming/scheduled notifications that haven't passed yet
        return (status === 'upcoming' || status === 'scheduled') && scheduledTime > now;
    };

    // Handle create new notification
    const handleCreateNotification = () => {
        // Set current date and time as default
        const now = new Date();
        const currentDate = now.toISOString().split('T')[0];
        const currentTime = now.toTimeString().slice(0, 5);

        setSelectedNotification({
            title: '',
            message: '',
            scheduledDate: currentDate,
            scheduledTime: currentTime,
            notificationType: '',
            isActive: true,
            navigateToPage: ''
        });
        setModalMode('create');
        setNotificationModal(true);
    };

    const handleFieldChange = (field, value) => {
        setSelectedNotification(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSaveNotification = () => {
        if (!selectedNotification) return;

        // Basic validation
        if (!selectedNotification.title?.trim()) {
            toast.error('Please enter a title');
            return;
        }
        if (!selectedNotification.message?.trim()) {
            toast.error('Please enter a message');
            return;
        }
        if (!selectedNotification.scheduledDate) {
            toast.error('Please select a scheduled date');
            return;
        }
        if (!selectedNotification.scheduledTime) {
            toast.error('Please select a scheduled time');
            return;
        }

        // Validate date/time is not in past
        const scheduledDateTime = new Date(`${selectedNotification.scheduledDate}T${selectedNotification.scheduledTime}`);
        if (scheduledDateTime < new Date()) {
            toast.error('Cannot schedule notification for past date/time');
            return;
        }

        setSaving(true);

        // Use same API for both create and update
        scheduleAutoNotification(selectedNotification)((response) => {
            setSaving(false);
            if (response?.status === 200 || response?.statusCode === 200 || response?.statusCode === 201) {
                toast.success('Notification saved successfully!');
                setUpdate(!update);
                setNotificationModal(false);
                setSelectedNotification(null);
            } else {
                toast.error(response?.data?.message || response?.message || 'Failed to save notification');
            }
        });
    };

    const handleCancel = () => {
        setNotificationModal(false);
        setSelectedNotification(null);
    };

    const deleteNotificationItem = (id) => {
        // deleteNotification(id)((response) => {
        //     if (response?.statusCode === 200) {
        //         toast.success('Notification deleted successfully!');
        //         setDeleteModal(false);
        //         setUpdate(!update);
        //     } else {
        //         toast.error(response?.message || 'Failed to delete notification');
        //     }
        // });
    }

    // Function to determine notification status for filtering
    const getNotificationStatus = (row) => {
        const now = new Date();
        const scheduledTime = new Date(row?.scheduledDateTime);

        if (row?.status?.toLowerCase() === "pending") {
            if (scheduledTime && scheduledTime > now) {
                return "upcoming";
            } else if (scheduledTime && scheduledTime < now) {
                return "expired";
            }
        }
        return row?.status?.toLowerCase();
    };

    // Check if active column should be shown based on current tab
    const shouldShowActiveColumn = () => {
        return activeTab === 'upcoming' || activeTab === 'scheduled';
    };

    // Combined title and message column
    const notificationContentColumn = {
        id: "notificationContent",
        name: "Notification",
        cell: (row) => (
            <Tooltip
                title={
                    <div>
                        <div><strong>Title:</strong> {row?.title}</div>
                        <div><strong>Message:</strong> {row?.message}</div>
                    </div>
                }
                disableInteractive
                placement="top"
            >
                <div style={{ width: "100%", cursor: "pointer", textAlign: "center" }} onClick={() => handleRowClick(row)}>
                    <div className="an-cell-title" style={{ marginLeft: "auto", marginRight: "auto" }}>
                        {row?.title || <span className="an-empty">—</span>}
                    </div>
                    <div className="an-cell-message" style={{ marginLeft: "auto", marginRight: "auto" }}>
                        {row?.message}
                    </div>
                </div>
            </Tooltip>
        ),
        sortable: true,
        minWidth: "220px",
        grow: 2,
        center: true,
    };
    // Status column
    const statusColumn = {
        id: "status",
        name: "Status",
        selector: (row) => row?.status,
        cell: (row) => {
            const now = new Date();
            const scheduledTime = new Date(row?.scheduledDateTime);
            let displayStatus = row?.status;

            if (row?.status?.toLowerCase() === "pending") {
                if (scheduledTime && scheduledTime > now) displayStatus = "Upcoming";
                else if (scheduledTime && scheduledTime < now) displayStatus = "Expired";
            }

            const getStatusStyle = (status) => {
                switch (status?.toLowerCase()) {
                    case "upcoming": return { background: "#dcfce7", color: "#166534" };
                    case "scheduled": return { background: "#e0f2fe", color: "#0369a1" };
                    case "sent": return { background: "#d1fae5", color: "#047857" };
                    case "failed": return { background: "#fee2e2", color: "#b91c1c" };
                    case "expired": return { background: "#fef3c7", color: "#92400e" };
                    default: return { background: "#f1f5f9", color: "#475569" };
                }
            };

            return (
                <div onClick={() => handleRowClick(row)} style={{ cursor: "pointer" }}>
                    <span className="an-status-chip" style={getStatusStyle(displayStatus)}>
                        {displayStatus}
                    </span>
                </div>
            );
        },
        sortable: true,
        width: "130px",
        center: true,
    };

    // Active status column (only shown for upcoming and scheduled tabs)
    const activeStatusColumn = {
        id: "isActive",
        name: "Active",
        cell: (row, index) => (
            <div className="d-flex justify-content-center">
                <div className="d-flex align-items-center">
                    <div className="d-flex flex-row align-items-center gap-3" role="button">
                        <div className="d-flex justify-content-center">
                            <Switch
                                size="20px"
                                id={`custom-switch-${index}`}
                                checked={row?.isActive}
                                onChange={() => statusActive(row?.id)}
                                className="custom-switch"
                            />
                        </div>
                    </div>
                </div>
            </div>
        ),
        center: true,
        width: "100px",
    };

    // Date column
    const dateColumn = {
        id: "scheduledDate",
        name: "Date",
        selector: (row) => row?.scheduledDate,
        cell: (row) => {
            const date = row?.scheduledDateTime ? new Date(row.scheduledDateTime) : null;
            return (
                <div style={{ cursor: "pointer" }} onClick={() => handleRowClick(row)}>
                    {date
                        ? <span className="an-cell-date">{date.toLocaleDateString([], { year: "numeric", month: "short", day: "numeric" })}</span>
                        : <span className="an-empty">—</span>}
                </div>
            );
        },
        sortable: true,
        width: "130px",
        center: true,
    };

    // Time column
    const timeColumn = {
        id: "scheduledTime",
        name: "Time",
        selector: (row) => row?.scheduledTime,
        cell: (row) => {
            const date = row?.scheduledDateTime ? new Date(row.scheduledDateTime) : null;
            return (
                <div style={{ cursor: "pointer" }} onClick={() => handleRowClick(row)}>
                    {date
                        ? <span className="an-cell-time">{date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                        : <span className="an-empty">—</span>}
                </div>
            );
        },
        sortable: true,
        width: "100px",
        center: true,
    };

    // Navigation column
    const navigationColumn = {
        id: "navigateToPage",
        name: "Navigation",
        selector: (row) => row?.navigateToPage,
        cell: (row) => (
            <div style={{ cursor: "pointer" }} onClick={() => handleRowClick(row)}>
                {row?.navigateToPage
                    ? <span className="an-nav-chip" title={row.navigateToPage}>{row.navigateToPage}</span>
                    : <span className="an-empty">—</span>}
            </div>
        ),
        sortable: true,
        width: "160px",
        center: true,
    };

    // Dynamic columns based on active tab
    const getColumns = () => {
        const baseColumns = [
            {
                name: "#",
                selector: (row, index) => (page - 1) * limit + (index + 1),
                cell: (row, index) => (
                    <span style={{ color: '#94a3b8', fontSize: '11px', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                        {(page - 1) * limit + (index + 1)}
                    </span>
                ),
                width: "60px",
                center: true,
            },
            notificationContentColumn,
            statusColumn,
            dateColumn,
            timeColumn,
            navigationColumn,
        ];

        // Only add active column for upcoming and scheduled tabs
        if (shouldShowActiveColumn()) {
            baseColumns.splice(5, 0, activeStatusColumn); // Insert after status column
        }

        return baseColumns;
    };

    const getNotificationData = async () => {
        setLoading(true);
        const filters = {
            page: page,
            limit: limit,
            search: search,
            sortBy: sortBy,
            sortDirection: sortDirection,
            status: activeTab === 'all' ? null : activeTab // Send null for 'all' to get all statuses
        };

        getAllNotification(filters)((response) => {
            if (response?.statusCode == 200) {
                const notifications = response?.data?.notifications || [];
                const total = response?.data?.totalItems || 0;

                setData(notifications);
                setTotalRows(total);
            } else {
                setData([]);
            }
            setLoading(false);
        });
    }

    useEffect(() => {
        getNotificationData();
        setIsInitialRender(false);
    }, [update, page, limit, sortBy, sortDirection, activeTab]);

    useEffect(() => {
        let timeout = setTimeout(() => {
            if (initial) setUpdate(!update);
            else setInitial(true);
        }, 500);

        return () => {
            clearTimeout(timeout);
        };
    }, [search]);

    useEffect(() => {
        if (!isInitialRender) {
            setPage(1);
            getNotificationData();
        }
    }, [activeTab]);

    const handleSort = (column, sortDirection) => {
        setSortBy(column.id);
        setSortDirection(sortDirection);
        setUpdate(!update);
    };

    const handlePerRowsChange = (newPerPage, page) => {
        setLimit(newPerPage);
        setPage(1);
        setUpdate(!update);
    };

    const handlePageChange = (page) => {
        setPage(page);
        setUpdate(!update);
    };

    const TABS = [
        { key: 'upcoming', label: 'Upcoming', icon: FiClock },
        { key: 'scheduled', label: 'Scheduled', icon: FiClock },
        { key: 'sent', label: 'Sent', icon: FiCheckCircle },
        { key: 'failed', label: 'Failed', icon: FiXCircle },
        { key: 'expired', label: 'Expired', icon: FiXCircle },
    ];

    const EMPTY_COPY = {
        upcoming: { title: 'No upcoming notifications', sub: 'Schedule a notification to see it appear here before it goes out.' },
        scheduled: { title: 'Nothing scheduled yet', sub: 'Scheduled notifications will show up here once they are queued.' },
        sent: { title: 'No sent notifications', sub: 'Notifications that have been delivered will appear in this tab.' },
        failed: { title: 'No failed notifications', sub: 'Any delivery failures will be listed here for review.' },
        expired: { title: 'No expired notifications', sub: 'Notifications past their scheduled time show up here.' },
    };
    const emptyCopy = EMPTY_COPY[activeTab] || { title: 'No notifications yet', sub: 'You have not scheduled any notifications yet.' };

    const emptyStateComponent = (
        <div className="an-empty-state">
            <div className="an-empty-icon"><FiBellOff /></div>
            <h6 className="an-empty-title">{emptyCopy.title}</h6>
            <p className="an-empty-sub">{emptyCopy.sub}</p>
        </div>
    );

    const getStatusMeta = (row) => {
        const raw = getNotificationStatus(row);
        const map = {
            upcoming:  { label: 'Upcoming',  accent: '#16a34a', iconBg: '#dcfce7', iconFg: '#15803d', pillBg: '#dcfce7', pillFg: '#166534', Icon: FiClock },
            scheduled: { label: 'Scheduled', accent: '#0284c7', iconBg: '#e0f2fe', iconFg: '#0369a1', pillBg: '#e0f2fe', pillFg: '#0369a1', Icon: FiCalendar },
            sent:      { label: 'Sent',      accent: '#059669', iconBg: '#d1fae5', iconFg: '#047857', pillBg: '#d1fae5', pillFg: '#047857', Icon: FiCheckCircle },
            failed:    { label: 'Failed',    accent: '#dc2626', iconBg: '#fee2e2', iconFg: '#b91c1c', pillBg: '#fee2e2', pillFg: '#b91c1c', Icon: FiXCircle },
            expired:   { label: 'Expired',   accent: '#d97706', iconBg: '#fef3c7', iconFg: '#92400e', pillBg: '#fef3c7', pillFg: '#92400e', Icon: FiAlertTriangle },
        };
        return map[raw] || { label: raw || 'Pending', accent: '#94a3b8', iconBg: '#f1f5f9', iconFg: '#64748b', pillBg: '#f1f5f9', pillFg: '#475569', Icon: FiBell };
    };

    const formatCardDate = (iso) => {
        if (!iso) return { date: '—', time: '' };
        const d = new Date(iso);
        return {
            date: d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }),
            time: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        };
    };

    return (
        <>
            <div className="auto-notification-page">
                <style>{`
                    .auto-notification-page { background: #f9fafb; min-height: calc(100vh - 70px); }
                    .main-content:has(.auto-notification-page) { background: #f9fafb; }
                    .page-content:has(.auto-notification-page) { background: #f9fafb; }
                    body:has(.auto-notification-page) .container-fluid,
                    body:has(.auto-notification-page) .container {
                        padding-left: 0 !important;
                        padding-right: 0 !important;
                    }
                    body:has(.auto-notification-page) .page-content,
                    body:has(.auto-notification-page) #layout-wrapper.no-topbar .page-content,
                    body:has(.auto-notification-page) #layout-wrapper .page-content {
                        padding-top: 0 !important;
                        padding-bottom: 24px !important;
                        padding-left: 16px !important;
                        padding-right: 16px !important;
                    }
                    .auto-notification-page h3 { font-size: 18px !important; }
                    .auto-notification-page .products-and-options .MuiPaper-root {
                        border: 1px solid #e2e8f0 !important;
                        border-radius: 10px !important;
                        overflow: hidden;
                    }
                    /* Sticky header */
                    .auto-notification-page .page-header-sticky {
                        padding: clamp(14px, 1.6vw, 24px) clamp(14px, 1.4vw, 20px) clamp(10px, 1.2vw, 18px) !important;
                        margin: 0 -16px 12px !important;
                        top: 0 !important;
                        background: #f9fafb !important;
                        border-bottom: 1px solid #eef2f7 !important;
                        box-shadow: none !important;
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 12px;
                        flex-wrap: nowrap;
                    }
                    .auto-notification-page .an-header-title {
                        margin: 0;
                        font-size: 18px;
                        font-weight: 700;
                        color: #111827;
                        letter-spacing: -0.01em;
                    }
                    .auto-notification-page .an-header-actions {
                        display: inline-flex;
                        align-items: center;
                        gap: 8px;
                        flex-wrap: wrap;
                    }
                    .auto-notification-page .an-btn {
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        gap: 6px;
                        padding: 0 14px;
                        height: 34px;
                        border-radius: 6px;
                        border: 1px solid transparent;
                        font-size: 12px;
                        font-weight: 600;
                        cursor: pointer;
                        transition: all 0.15s ease;
                        white-space: nowrap;
                    }
                    .auto-notification-page .an-btn svg { width: 14px; height: 14px; }
                    .auto-notification-page .an-btn-secondary { background: #fff; border-color: #e2e8f0; color: #475569; }
                    .auto-notification-page .an-btn-secondary:hover:not(:disabled) { border-color: #008695; color: #008695; background: rgba(0,134,149,0.06); }
                    .auto-notification-page .an-btn-primary { background: #008695; border-color: #008695; color: #fff; }
                    .auto-notification-page .an-btn-primary:hover:not(:disabled) { background: #007784; border-color: #007784; }
                    @media (max-width: 991px) {
                        .auto-notification-page .page-header-sticky { padding: 16px 16px 12px !important; }
                    }
                    @media (max-width: 767.98px) {
                        .auto-notification-page .page-header-sticky {
                            padding: 10px 16px 8px !important;
                            margin: 0 -16px 8px !important;
                            position: static !important;
                            gap: 8px;
                        }
                        .auto-notification-page .an-header-title { font-size: 15px; }
                        .auto-notification-page .an-header-actions { gap: 6px; flex-shrink: 0; }
                        .auto-notification-page .an-btn { width: 32px; height: 32px; padding: 0; flex: 0 0 auto; }
                        .auto-notification-page .an-btn .an-btn-label { display: none; }
                        .auto-notification-page .an-btn svg { width: 15px; height: 15px; }
                    }
                    @media (max-width: 380px) {
                        .auto-notification-page .an-header-title { font-size: 14px; }
                        .auto-notification-page .an-btn { width: 30px; height: 30px; }
                    }

                    /* Segmented underline tabs (matches Banner page) */
                    .auto-notification-page .an-tabs {
                        display: flex;
                        align-items: center;
                        gap: 4px;
                        margin: 0 0 12px;
                        border-bottom: 1px solid #e2e8f0;
                        overflow-x: auto;
                        scrollbar-width: none;
                    }
                    .auto-notification-page .an-tabs::-webkit-scrollbar { display: none; }
                    .auto-notification-page .an-tab {
                        display: inline-flex;
                        align-items: center;
                        gap: 5px;
                        padding: 8px 14px;
                        border: 0;
                        border-bottom: 2px solid transparent;
                        margin-bottom: -1px;
                        background: transparent;
                        color: #64748b;
                        font-size: 12px;
                        font-weight: 600;
                        cursor: pointer;
                        white-space: nowrap;
                        transition: color 0.15s ease, border-color 0.15s ease;
                    }
                    .auto-notification-page .an-tab:hover:not(.is-active) { color: #0f172a; }
                    .auto-notification-page .an-tab.is-active { color: #008695; border-bottom-color: #008695; }
                    .auto-notification-page .an-tab svg { width: 13px; height: 13px; }
                    @media (max-width: 767.98px) {
                        .auto-notification-page .an-tabs { gap: 2px; }
                        .auto-notification-page .an-tab { padding: 8px 12px; font-size: 12px; }
                    }
                    @media (max-width: 480px) {
                        .auto-notification-page .an-tab { padding: 7px 10px; font-size: 11.5px; }
                    }

                    /* Status badge inside the Status column */
                    .auto-notification-page .an-status-chip {
                        display: inline-flex;
                        align-items: center;
                        padding: 3px 10px;
                        border-radius: 999px;
                        font-size: 10px;
                        font-weight: 600;
                        letter-spacing: 0.01em;
                        text-transform: capitalize;
                        white-space: nowrap;
                    }
                    .auto-notification-page .an-nav-chip {
                        display: inline-block;
                        padding: 3px 10px;
                        background: #eef2ff;
                        color: #4338ca;
                        border-radius: 6px;
                        font-size: 10px;
                        font-weight: 600;
                        max-width: 140px;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }
                    .auto-notification-page .an-nav-empty,
                    .auto-notification-page .an-empty {
                        color: #94a3b8;
                        font-size: 11px;
                    }
                    .auto-notification-page .an-cell-title {
                        color: #0f172a;
                        font-size: 12px;
                        font-weight: 600;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        max-width: 240px;
                    }
                    .auto-notification-page .an-cell-message {
                        color: #64748b;
                        font-size: 11px;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        max-width: 240px;
                        margin-top: 2px;
                    }
                    .auto-notification-page .an-cell-date { color: #0f172a; font-size: 11px; font-weight: 600; }
                    .auto-notification-page .an-cell-time { color: #475569; font-size: 11px; font-variant-numeric: tabular-nums; }
                    .auto-notification-page .rdt_Pagination { color: #475569 !important; font-size: 12px !important; }

                    /* Mobile card list (hidden by default; shown below 768px) */
                    .auto-notification-page .an-mobile-list { display: none; }
                    @media (max-width: 767.98px) {
                        .auto-notification-page .an-desktop-table { display: none !important; }
                        .auto-notification-page .an-mobile-list {
                            display: flex;
                            flex-direction: column;
                            gap: 10px;
                            padding: 4px 0 24px;
                        }
                    }

                    .auto-notification-page .an-card {
                        position: relative;
                        display: flex;
                        flex-direction: column;
                        gap: 10px;
                        padding: 14px 14px 14px 20px;
                        background: #fff;
                        border: 1px solid #e2e8f0;
                        border-radius: 12px;
                        box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
                        overflow: hidden;
                        cursor: pointer;
                        transition: transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
                    }
                    .an-card:active { transform: scale(0.99); }
                    .an-card:hover { border-color: #cbd5e1; box-shadow: 0 4px 10px rgba(15, 23, 42, 0.06); }

                    /* Left accent stripe — color set inline based on status */
                    .an-card::before {
                        content: '';
                        position: absolute;
                        left: 0; top: 0; bottom: 0;
                        width: 5px;
                        background: var(--an-accent, #94a3b8);
                    }

                    .an-card-top {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 8px;
                    }
                    .an-card-status-block {
                        display: inline-flex;
                        align-items: center;
                        gap: 8px;
                        min-width: 0;
                    }
                    .an-card-icon {
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        width: 34px;
                        height: 34px;
                        border-radius: 10px;
                        background: var(--an-icon-bg, #f1f5f9);
                        color: var(--an-icon-fg, #64748b);
                        flex-shrink: 0;
                    }
                    .an-card-icon svg { width: 16px; height: 16px; }
                    .an-card-status-pill {
                        display: inline-flex;
                        align-items: center;
                        padding: 3px 9px;
                        border-radius: 999px;
                        font-size: 10px;
                        font-weight: 600;
                        letter-spacing: 0.01em;
                        text-transform: capitalize;
                        white-space: nowrap;
                    }
                    .an-card-toggle {
                        display: inline-flex;
                        align-items: center;
                        flex-shrink: 0;
                    }
                    .an-card-toggle .MuiSwitch-root { margin: 0; }

                    .an-card-body { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
                    .an-card-title {
                        margin: 0;
                        font-size: 13px;
                        font-weight: 700;
                        color: #0f172a;
                        line-height: 1.3;
                        letter-spacing: -0.01em;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        display: -webkit-box;
                        -webkit-line-clamp: 2;
                        -webkit-box-orient: vertical;
                    }
                    .an-card-message {
                        margin: 0;
                        font-size: 11.5px;
                        color: #64748b;
                        line-height: 1.45;
                        display: -webkit-box;
                        -webkit-line-clamp: 2;
                        -webkit-box-orient: vertical;
                        overflow: hidden;
                    }

                    .an-card-foot {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 10px;
                        padding-top: 6px;
                        border-top: 1px dashed #e2e8f0;
                    }
                    .an-card-when {
                        display: inline-flex;
                        align-items: center;
                        gap: 6px;
                        color: #475569;
                        font-size: 11px;
                        font-weight: 500;
                        font-variant-numeric: tabular-nums;
                    }
                    .an-card-when svg { width: 12px; height: 12px; color: #94a3b8; }
                    .an-card-when-dot { color: #cbd5e1; margin: 0 4px; }
                    .an-card-nav {
                        display: inline-flex;
                        align-items: center;
                        gap: 4px;
                        padding: 3px 9px;
                        background: #eef2ff;
                        color: #4338ca;
                        border-radius: 999px;
                        font-size: 10px;
                        font-weight: 600;
                        max-width: 60%;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }
                    .an-card-nav svg { width: 11px; height: 11px; flex-shrink: 0; }
                    .an-card-nav-empty { font-size: 10px; color: #94a3b8; }

                    /* Empty state (mobile) */
                    .auto-notification-page .an-mobile-list .an-empty-state {
                        min-height: 320px;
                        border-radius: 12px;
                        border: 1px solid #e2e8f0;
                    }

                    /* Empty state */
                    .auto-notification-page .an-empty-state {
                        width: 100%;
                        min-height: 380px;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        padding: 40px 24px;
                        text-align: center;
                        background: linear-gradient(180deg, #fafbfd 0%, #f5f7fa 100%);
                    }
                    .auto-notification-page .an-empty-icon {
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        width: 72px;
                        height: 72px;
                        border-radius: 50%;
                        background: #eef2f7;
                        color: #94a3b8;
                        margin-bottom: 16px;
                        box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);
                    }
                    .auto-notification-page .an-empty-icon svg { width: 30px; height: 30px; }
                    .auto-notification-page .an-empty-title {
                        margin: 0 0 6px;
                        font-size: 15px;
                        font-weight: 700;
                        color: #0f172a;
                        letter-spacing: -0.01em;
                    }
                    .auto-notification-page .an-empty-sub {
                        margin: 0;
                        max-width: 320px;
                        font-size: 12px;
                        line-height: 1.5;
                        color: #94a3b8;
                        font-weight: 500;
                    }
                `}</style>

                <div className="page-header-sticky">
                    <h3 className="an-header-title">Auto Notifications</h3>
                    <div className="an-header-actions">
                        <button
                            type="button"
                            className="an-btn an-btn-secondary"
                            onClick={() => setShowBulkModal(true)}
                        >
                            <FiFilePlus />
                            <span className="an-btn-label">Add Bulk</span>
                        </button>
                        <button
                            type="button"
                            className="an-btn an-btn-primary"
                            onClick={handleCreateNotification}
                        >
                            <FiCalendar />
                            <span className="an-btn-label">Schedule Notification</span>
                        </button>
                    </div>
                </div>

                <div className="an-tabs" role="tablist">
                    {TABS.map(({ key, label, icon: Icon }) => (
                        <button
                            key={key}
                            type="button"
                            role="tab"
                            aria-selected={activeTab === key}
                            className={`an-tab${activeTab === key ? ' is-active' : ''}`}
                            onClick={() => setActiveTab(key)}
                        >
                            <Icon />
                            {label}
                        </button>
                    ))}
                </div>

                {/* Delete Confirmation Modal */}
                <CustomisedModal
                    isOpen={deleteModal}
                    customClass={"policy_modal"}
                    toggle={toggleDeleteModal}
                >
                    <h5>Are you sure you want to delete this notification?</h5>
                    <div className="policy_Agree">
                        <Button
                            onClick={() => setDeleteModal(!deleteModal)}
                            size="lg"
                            className='btn_cancel'
                            disabled={saving}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="lg"
                            className='btn'
                            onClick={() => deleteNotificationItem(deletingId)}
                            disabled={saving}
                        >
                            {saving ? 'Deleting...' : 'Delete'}
                        </Button>
                    </div>
                </CustomisedModal>

                <Box className="products-and-options an-desktop-table" sx={{ mt: 0 }}>
                    <Paper square className='shadow-none border-0'>
                        <DataTable
                            columns={getColumns()}
                            data={data}
                            progressPending={loading}
                            progressComponent={<div style={{ height: '60vh' }}><Loader /></div>}
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            paginationRowsPerPageOptions={[10, 50, 100, 150]}
                            paginationPerPage={limit}
                            onChangeRowsPerPage={handlePerRowsChange}
                            onChangePage={handlePageChange}
                            onSort={handleSort}
                            sortServer
                            currentPage={page}
                            customStyles={queueDatatableCustomStyles}
                            onRowClicked={handleRowClick}
                            pointerOnHover
                            highlightOnHover
                            noDataComponent={emptyStateComponent}
                        />
                    </Paper>
                </Box>

                {/* Mobile: creative message-card list */}
                <div className="an-mobile-list">
                    {loading ? (
                        <div style={{ height: '40vh' }}><Loader /></div>
                    ) : data && data.length > 0 ? (
                        data.map((row) => {
                            const meta = getStatusMeta(row);
                            const { date, time } = formatCardDate(row?.scheduledDateTime);
                            const StatusIcon = meta.Icon;
                            const canToggle = shouldShowActiveColumn();
                            return (
                                <div
                                    key={row?.id}
                                    className="an-card"
                                    onClick={() => handleRowClick(row)}
                                    style={{ '--an-accent': meta.accent, '--an-icon-bg': meta.iconBg, '--an-icon-fg': meta.iconFg }}
                                >
                                    <div className="an-card-top">
                                        <div className="an-card-status-block">
                                            <span className="an-card-icon"><StatusIcon /></span>
                                            <span className="an-card-status-pill" style={{ background: meta.pillBg, color: meta.pillFg }}>
                                                {meta.label}
                                            </span>
                                        </div>
                                        {canToggle && (
                                            <div className="an-card-toggle" onClick={(e) => e.stopPropagation()}>
                                                <Switch
                                                    size="small"
                                                    checked={!!row?.isActive}
                                                    onChange={() => statusActive(row?.id)}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    <div className="an-card-body">
                                        <h6 className="an-card-title">{row?.title || 'Untitled notification'}</h6>
                                        {row?.message && <p className="an-card-message">{row.message}</p>}
                                    </div>

                                    <div className="an-card-foot">
                                        <span className="an-card-when">
                                            <FiCalendar />
                                            {date}
                                            <span className="an-card-when-dot">·</span>
                                            {time}
                                        </span>
                                        {row?.navigateToPage ? (
                                            <span className="an-card-nav" title={row.navigateToPage}>
                                                <FiArrowRight />
                                                {row.navigateToPage}
                                            </span>
                                        ) : (
                                            <span className="an-card-nav-empty">No target page</span>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        emptyStateComponent
                    )}
                </div>
            </div>

            <CreateUpdateAutoNotificationModal
                show={notificationModal}
                onHide={handleCancel}
                notification={selectedNotification}
                onSave={handleSaveNotification}
                onCancel={handleCancel}
                onFieldChange={handleFieldChange}
                saving={saving}
            />

            <BulkNotificationModal
                show={showBulkModal}
                onHide={() => setShowBulkModal(false)}
                onUpload={() => setUpdate(!update)}
            />
        </>
    )
}

export default AutoNotification;